import { Injectable } from "@angular/core";
import { ApicallService } from "@core-services/apicall.service";
import { Observable, ReplaySubject, timer, map, share } from "rxjs";
import { parseApiObject, parseApiResponseObject } from "@helpers/functions";
import { AccountService } from "@modules/account/services/account.service";
import { PortfolioOverview } from "@modules/portfolio/domain/entities/portfolio-overview.model";
import { PortfolioPerformance } from "@modules/portfolio/domain/entities/portfolio-performance.model";
import { AssetDetail } from "@modules/tradable/tradable-detail/domain/asset-detail.model";
import { ApiEndpoint } from "@core-services/api-endpoint.model";
import { CrossSellingRecommendation } from "../domain/entities/portfolio-recommendation.model";
import { AssetDetailPriceChange } from "@modules/tradable/tradable-detail/domain/asset-detail-price-change.model";
import { Timespan } from "@helpers/const/timespan";
import { Dictionary } from "@helpers/types";

@Injectable({
	providedIn: "root"
})
export class PortfolioRemote {
	private readonly performanceCachedHttpCalls: Dictionary<Observable<PortfolioPerformance>> = {};
	private readonly overviewCachedHttpCalls: Dictionary<Observable<PortfolioOverview>> = {};

	constructor(
		private readonly apiCallService: ApicallService,
		private readonly accountService: AccountService
	) {}

	public getPortfolioOverview(): Observable<PortfolioOverview> {
		const fiatId = this.accountService.getDefaultFiat().getId();

		if (!this.overviewCachedHttpCalls[fiatId]) {
			this.overviewCachedHttpCalls[fiatId] = this.apiCallService
				.get(`${ApiEndpoint.portfolio}/overview?fiat_id=${fiatId}`)
				.pipe(map((overview) => parseApiObject<PortfolioOverview>(overview)));
		}

		return this.overviewCachedHttpCalls[fiatId];
	}

	public getPortfolioPerformance(timeframe: string): Observable<PortfolioPerformance> {
		const fiatId = this.accountService.getDefaultFiat().getId();

		if (timeframe === Timespan.SixMonths) {
			timeframe = "six_month"; // fix for this API request
		}

		if (!this.performanceCachedHttpCalls[timeframe + fiatId]) {
			this.performanceCachedHttpCalls[timeframe + fiatId] = this.apiCallService
				.get(`${ApiEndpoint.portfolio}/performance/${timeframe}?fiat_id=${fiatId}`)
				.pipe(
					map((performance) => parseApiObject<PortfolioPerformance>(performance)),
					share({
						// the main star 😉
						connector: () => new ReplaySubject(1), // override default Subject (why?)
						resetOnComplete: () => timer(2000) // invalidate cache on timeout
					})
				);
		}

		return this.performanceCachedHttpCalls[timeframe + fiatId];
	}

	public getAssetDetails(uuid: string): Observable<AssetDetail> {
		const fiatId = this.accountService.getDefaultFiat().getId();

		return this.apiCallService
			.get(`${ApiEndpoint.portfolio}/asset/${uuid}?fiat_id=${fiatId}`)
			.pipe(map((val) => parseApiResponseObject<AssetDetail>(val)));
	}

	public getAssetDetailsPriceChange(uuid: string): Observable<AssetDetailPriceChange> {
		return this.apiCallService.get(`assets/details/${uuid}`);
	}

	public getCrossSellingSuggestions(): Observable<CrossSellingRecommendation> {
		return this.apiCallService
			.get(`${ApiEndpoint.portfolio}/recommendations`)
			.pipe(map((performance) => parseApiResponseObject<CrossSellingRecommendation>(performance)));
	}
}
